"use client";

import { useState } from "react";
import Link from "next/link";
import { ArrowRight, CaretDown } from "@phosphor-icons/react/dist/ssr";

import { cn } from "@/src/lib/utils";
import { Button } from "@/src/components/ui/Button";
import { Heading, Text } from "@/src/components/ui/Typography";

export interface NavDropdownItem {
  label: string;
  href: string;
}

/** A titled group of links inside one mega-menu column. */
export interface NavDropdownCluster {
  heading?: string;
  items: NavDropdownItem[];
}

interface NavDropdownProps {
  label: string;
  items: NavDropdownItem[];
  /** When given, renders a 3-column mega menu instead of the single flat list, taking precedence over `items`. */
  columns?: NavDropdownCluster[][];
  /** Short intro shown next to the title in the mega-menu's left rail. Ignored outside `columns` mode. */
  description?: string;
  active?: boolean;
  dark?: boolean;
}

export function NavDropdown({
  label,
  items,
  columns,
  description,
  active,
  dark,
}: NavDropdownProps) {
  // Hover-driven, but tracked as real state (not pure CSS `group-hover`) so
  // a link click can force the panel closed immediately — otherwise it
  // stays visible (still "hovered") through the navigation until the mouse
  // physically leaves the area.
  const [open, setOpen] = useState(false);

  // flex h-full items-center: stretches the hover-trigger box to the full
  // header height (nav gives it the room — see the comment there) instead
  // of just the trigger text's own line height, so there's no gap of
  // unhovered space between the trigger and the dropdown panel below it
  // for the cursor to lose hover crossing through.
  return (
    <div
      className="relative flex h-full items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <Button
        type="button"
        variant="ghost"
        size="auto"
        aria-expanded={open}
        className={cn(
          "transition-colors duration-300",
          active
            ? "text-brand"
            : dark
              ? "text-neutral-50 hover:text-brand"
              : "text-neutral-700 hover:text-brand",
        )}
      >
        <Text as="span" size="bodySMMedium" className={cn(
          "transition-colors duration-300",
          active
            ? "text-brand"
            : dark
              ? "text-neutral-50 font-light"
              : "text-neutral-700 font-light",
        )}>
          {label}
        </Text>

        <CaretDown
          size={14}
          weight="regular"
          className={cn(
            "transition-transform duration-300",
            open && "rotate-180",
            dark ? "fill-neutral-50" : "fill-neutral-700",
          )}
        />
      </Button>

      <div
        className={cn(
          "shadow-lg transition-all duration-200",
          open ? "visible translate-y-0 opacity-100" : "invisible translate-y-2 opacity-0",
          dark ? "border-neutral-600 bg-neutral-700" : "border-neutral-200 bg-white",
          columns
            ? // Fixed rather than absolute so this isn't sized/positioned
              // relative to its own (trigger-width) wrapper — `<header>`
              // carries a `transform` (its scroll-hide translate-y, even at
              // rest that's translate-y-0, not "none") which per spec makes
              // it the containing block for fixed-position descendants
              // instead of the viewport. Since the header is full-width,
              // that lets `left-16 right-16` line up with the header's own
              // xl:px-16 content padding — the mega menu reads as spanning
              // the page rather than dropping from one nav item, matching
              // the reference. (Nav itself is `xl:flex`-only, so the mega
              // menu can never be visible below that breakpoint anyway —
              // no need to match the header's other breakpoints' padding.)
              "fixed left-16 right-16 top-(--header-height) rounded-b-md border-x border-b"
            : "absolute left-0 top-8 w-64 rounded-md border py-3",
        )}
      >
        {columns ? (
          <div className="mx-auto flex max-w-max-width gap-10 p-8">
            <div
              className={cn(
                "w-56 shrink-0 border-r pr-8",
                dark ? "border-neutral-600" : "border-neutral-300",
              )}
            >
              <Heading as="h3" size="headingMD" className={dark ? "text-neutral-50" : undefined}>
                {label}
              </Heading>

              {description && (
                <Text
                  size="bodySM"
                  className={dark ? "mt-3 text-neutral-400" : "mt-3 text-neutral-500"}
                >
                  {description}
                </Text>
              )}
            </div>

            <div className="grid flex-1 grid-cols-3 gap-x-10">
              {columns.map((column, columnIndex) => (
                <div key={columnIndex} className="flex flex-col gap-8">
                  {column.map((cluster, clusterIndex) => (
                    <div key={cluster.heading ?? clusterIndex} className="flex flex-col">
                      {cluster.heading && (
                        <div
                          className={cn(
                            "flex items-center gap-2  pb-3",
                            dark ? "border-neutral-600" : "border-neutral-300",
                          )}
                        >
                          <div aria-hidden className="h-4 w-1 shrink-0 rounded-full bg-brand" />
                          <Text
                            as="span"
                            size="labelLG"
                            className={cn(
                              "uppercase tracking-wider",
                              dark ? "text-neutral-50" : "text-neutral-700",
                            )}
                          >
                            {cluster.heading}
                          </Text>
                        </div>
                      )}
                      {cluster.items.map((item) => (
                        <Link
                          key={item.href}
                          href={item.href}
                          onClick={() => setOpen(false)}
                          className={cn(
                            "group/link flex items-center justify-between gap-2 py-2.5 transition-colors duration-200 hover:text-brand",
                            dark ? "text-neutral-300" : "text-neutral-600",
                          )}
                        >
                          <Text as="span" size="labelMD" className="text-inherit">
                            {item.label}
                          </Text>
                          <ArrowRight
                            size={14}
                            className="shrink-0 -translate-x-1 opacity-0 transition-all duration-200 group-hover/link:translate-x-0 group-hover/link:opacity-100"
                          />
                        </Link>
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ) : (
          items.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              onClick={() => setOpen(false)}
              className={cn(
                "block px-5 py-2.5 transition-colors duration-200 hover:text-brand",
                dark ? "text-neutral-300" : "text-neutral-600",
              )}
            >
              <Text as="span" size="labelMD" className="text-inherit">
                {item.label}
              </Text>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}
